import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileText,
  Edit,
  Trash2,
  Eye,
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Loader,
  Plus,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface Template {
  id: string;
  name: string;
  description: string;
  structure: {
    sections: Array<{
      id: string;
      title: string;
      type: string;
      order: number;
      properties?: Record<string, any>;
    }>;
    metadata: Record<string, any>;
  };
  createdAt: string;
  updatedAt: string;
}

const TemplateManager: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadTemplates();
    }
  }, [authLoading, isAuthenticated]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token'); // Support both for backwards compatibility
      if (!token || !isAuthenticated) {
        // Don't show error if auth is still loading
        if (!authLoading) {
          console.warn('Not authenticated or no token');
        }
        return;
      }

      console.log('Loading templates from /api/templates');
      const response = await fetch('/api/templates', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Template response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to load templates:', errorData);
        
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          // Token expired or invalid - clear it and redirect to login
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token'); // Remove both for compatibility
          localStorage.removeItem('user');
          toast.error('Your session has expired. Please log in again.');
          window.location.href = '/login';
          return;
        }
        
        throw new Error(errorData.error || `Failed to load templates (${response.status})`);
      }

      const data = await response.json();
      console.log('Templates loaded:', data.templates?.length || 0);
      setTemplates(data.templates || []);
    } catch (error: any) {
      console.error('Error loading templates:', error);
      toast.error(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'text/plain', 'text/markdown'];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.md')) {
      toast.error('Invalid file type. Please upload a PDF, TXT, or MD file.');
      return;
    }

    setUploading(true);
    setUploadProgress(10); // Start at 10% to show upload started

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('description', `Template generated from ${file.name}`);

      console.log('Starting template upload for:', file.name);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('Template upload timeout after 2 minutes');
        controller.abort();
      }, 120000); // 2 minute timeout

      setUploadProgress(30); // Uploading
      
      const response = await fetch('/api/templates/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token')}`
        },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      setUploadProgress(70); // Processing response

      if (!response.ok) {
        const error = await response.json();
        
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token'); // Remove both for compatibility
          localStorage.removeItem('user');
          toast.error('Your session has expired. Please log in again.');
          window.location.href = '/login';
          return;
        }
        
        throw new Error(error.error || 'Failed to create template');
      }

      const data = await response.json();
      
      setUploadProgress(100); // Complete
      
      console.log('Template created successfully:', data.template?.id);
      
      toast.success('Template created successfully!');
      setShowUploadModal(false);
      setUploading(false);
      setUploadProgress(0);
      
      await loadTemplates();
      
      // Show preview of created template
      setSelectedTemplate(data.template);
      setShowPreviewModal(true);
      
      // Reset file input
      event.target.value = '';
    } catch (error: any) {
      console.error('Error uploading template:', error);
      
      // Handle timeout
      if (error.name === 'AbortError') {
        toast.error('Request timed out. The template creation is taking longer than expected. Please try again.');
      } else {
        toast.error(error.message || 'Failed to create template');
      }
      
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDelete = async (template: Template) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/templates/${template.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete template');
      }

      toast.success('Template deleted successfully');
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(JSON.parse(JSON.stringify(template))); // Deep copy
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate) return;

    try {
      const response = await fetch(`/api/templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token')}`
        },
        body: JSON.stringify(editingTemplate)
      });

      if (!response.ok) {
        throw new Error('Failed to update template');
      }

      toast.success('Template updated successfully');
      setShowEditModal(false);
      setEditingTemplate(null);
      await loadTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  const renderTemplateStructure = (template: Template) => {
    return (
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-gray-900 mb-2">Sections ({template.structure.sections.length})</h4>
          <div className="space-y-2">
            {template.structure.sections.map((section, index) => (
              <div
                key={section.id}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded">
                      {index + 1}
                    </span>
                    <span className="font-medium text-gray-900">{section.title}</span>
                    <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                      {section.type}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {template.structure.metadata && Object.keys(template.structure.metadata).length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Metadata</h4>
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <pre className="text-xs overflow-x-auto">
                {JSON.stringify(template.structure.metadata, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Templates</h2>
          <p className="text-gray-600 mt-1">
            Manage content templates. Upload example documents to generate templates.
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
        >
          <Upload className="w-5 h-5" />
          <span>Upload Template</span>
        </button>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
          <p className="text-gray-600 mb-4">
            Upload an example document to create your first template
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all"
          >
            Upload Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => {
                      setSelectedTemplate(template);
                      setShowPreviewModal(true);
                    }}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(template)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template)}
                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                {template.structure.sections.length} sections • Created {new Date(template.createdAt).toLocaleDateString()}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => {
                if (!uploading) {
                  setShowUploadModal(false);
                }
              }}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto mt-20 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Upload Template Document</h3>
                <button
                  onClick={() => {
                    if (!uploading) {
                      setShowUploadModal(false);
                    } else {
                      // Cancel upload
                      if (window.confirm('Upload in progress. Cancel anyway?')) {
                        setUploading(false);
                        setUploadProgress(0);
                        setShowUploadModal(false);
                      }
                    }
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  disabled={false}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Document
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-2">
                      Upload a PDF, TXT, or MD file as an example
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.txt,.md"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      id="template-upload"
                    />
                    <label
                      htmlFor="template-upload"
                      className={cn(
                        "inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors",
                        uploading && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {uploading ? 'Processing...' : 'Choose File'}
                    </label>
                  </div>
                </div>

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Analyzing document structure...</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-[#667eea] to-[#764ba2] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">How it works:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Upload an example document (PDF, TXT, or MD)</li>
                        <li>AI analyzes the document structure and visual layout</li>
                        <li>A template is generated based on the structure</li>
                        <li>You can review and edit the generated template</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreviewModal && selectedTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setShowPreviewModal(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto mt-20 max-h-[80vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedTemplate.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedTemplate.description}</p>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {renderTemplateStructure(selectedTemplate)}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && editingTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-hidden"
          >
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setShowEditModal(false);
                setEditingTemplate(null);
              }}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto mt-20 max-h-[80vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Edit Template</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTemplate(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={editingTemplate.name}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editingTemplate.description}
                    onChange={(e) =>
                      setEditingTemplate({ ...editingTemplate, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Template Sections ({editingTemplate.structure.sections.length})
                      </label>
                      <button
                        onClick={() => {
                          const updated = { ...editingTemplate };
                          const newSection = {
                            id: `section-${Date.now()}`,
                            title: 'New Section',
                            type: 'content',
                            order: updated.structure.sections.length + 1,
                            properties: {}
                          };
                          updated.structure.sections = [...updated.structure.sections, newSection];
                          setEditingTemplate(updated);
                        }}
                        className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Section</span>
                      </button>
                    </div>
                    
                    <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                      {editingTemplate.structure.sections.map((section, index) => (
                        <div key={section.id} className="bg-white rounded-lg p-4 border border-gray-300 shadow-sm">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Section {index + 1}: Title
                                </label>
                                <input
                                  type="text"
                                  value={section.title}
                                  onChange={(e) => {
                                    const updated = { ...editingTemplate };
                                    updated.structure.sections[index].title = e.target.value;
                                    setEditingTemplate(updated);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Enter section title"
                                />
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                                  <select
                                    value={section.type}
                                    onChange={(e) => {
                                      const updated = { ...editingTemplate };
                                      updated.structure.sections[index].type = e.target.value;
                                      setEditingTemplate(updated);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="header">Header</option>
                                    <option value="content">Content</option>
                                    <option value="image">Image</option>
                                    <option value="list">List</option>
                                    <option value="table">Table</option>
                                    <option value="step">Step</option>
                                  </select>
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Display Order</label>
                                  <input
                                    type="number"
                                    value={section.order}
                                    onChange={(e) => {
                                      const updated = { ...editingTemplate };
                                      updated.structure.sections[index].order = parseInt(e.target.value) || 0;
                                      setEditingTemplate(updated);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    min="0"
                                  />
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col space-y-2 pt-6">
                              {index > 0 && (
                                <button
                                  onClick={() => {
                                    const updated = { ...editingTemplate };
                                    const sections = [...updated.structure.sections];
                                    [sections[index], sections[index - 1]] = [sections[index - 1], sections[index]];
                                    // Update order numbers
                                    sections.forEach((s, i) => s.order = i + 1);
                                    updated.structure.sections = sections;
                                    setEditingTemplate(updated);
                                  }}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Move up"
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </button>
                              )}
                              {index < editingTemplate.structure.sections.length - 1 && (
                                <button
                                  onClick={() => {
                                    const updated = { ...editingTemplate };
                                    const sections = [...updated.structure.sections];
                                    [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
                                    // Update order numbers
                                    sections.forEach((s, i) => s.order = i + 1);
                                    updated.structure.sections = sections;
                                    setEditingTemplate(updated);
                                  }}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Move down"
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (confirm(`Delete "${section.title}"?`)) {
                                    const updated = { ...editingTemplate };
                                    updated.structure.sections = updated.structure.sections.filter((_, i) => i !== index);
                                    // Reorder remaining sections
                                    updated.structure.sections.forEach((s, i) => s.order = i + 1);
                                    setEditingTemplate(updated);
                                  }
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete section"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {editingTemplate.structure.sections.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm">No sections yet. Click "Add Section" to get started.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Layout & Style Settings</label>
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Section Spacing</label>
                        <select
                          value={editingTemplate.structure.metadata?.sectionSpacing || 'normal'}
                          onChange={(e) => {
                            const updated = { ...editingTemplate };
                            if (!updated.structure.metadata) updated.structure.metadata = {};
                            updated.structure.metadata.sectionSpacing = e.target.value;
                            setEditingTemplate(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="compact">Compact</option>
                          <option value="normal">Normal</option>
                          <option value="spacious">Spacious</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Image Alignment</label>
                        <select
                          value={editingTemplate.structure.metadata?.imageAlignment || 'center'}
                          onChange={(e) => {
                            const updated = { ...editingTemplate };
                            if (!updated.structure.metadata) updated.structure.metadata = {};
                            updated.structure.metadata.imageAlignment = e.target.value;
                            setEditingTemplate(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                          <option value="full-width">Full Width</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Title Placement</label>
                        <select
                          value={editingTemplate.structure.metadata?.titlePlacement || 'top'}
                          onChange={(e) => {
                            const updated = { ...editingTemplate };
                            if (!updated.structure.metadata) updated.structure.metadata = {};
                            updated.structure.metadata.titlePlacement = e.target.value;
                            setEditingTemplate(updated);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="top">Top</option>
                          <option value="center">Center</option>
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTemplate(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190]"
                >
                  <Save className="w-4 h-4 inline mr-2" />
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TemplateManager;

